import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Search, Star, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const navigate = useNavigate();
  const showAuth = searchParams.get('auth');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, citiesRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/cities`),
      ]);
      setCategories(categoriesRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (selectedCity) params.append('city', selectedCity);
    navigate(`/businesses?${params.toString()}`);
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/businesses?category=${categoryId}`);
  };

  const categoryImages = {
    'restaurants': 'https://images.unsplash.com/photo-1762087577613-978bf9066d39?w=400',
    'coaching': 'https://images.unsplash.com/photo-1755548260148-60604bf6f588?w=400',
    'gyms': 'https://images.unsplash.com/photo-1761971975858-c487bc10daab?w=400',
    'salons': 'https://images.unsplash.com/photo-1746723378067-83a345ff3160?w=400',
    'hotels': 'https://images.pexels.com/photos/3754595/pexels-photo-3754595.jpeg?w=400',
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section
        className="relative h-[600px] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1764852666619-021c8e26f59f?w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 to-slate-900/20"></div>
        <div className="relative z-10 container mx-auto px-4 md:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight leading-none"
            data-testid="hero-title"
          >
            Discover Local Businesses
            <br />
            <span className="text-accent">Near You</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subtitle"
          >
            Connect with trusted businesses across Northeast India's tier-2 and tier-3 cities
          </motion.p>

          {/* Search Bar */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            onSubmit={handleSearch}
            className="max-w-4xl mx-auto"
            data-testid="search-form"
          >
            <div className="bg-white rounded-full shadow-lg p-2 flex flex-col md:flex-row gap-2">
              <div className="flex-1 flex items-center px-4">
                <Search className="w-5 h-5 text-slate-400 mr-2" />
                <Input
                  type="text"
                  placeholder="Search for restaurants, gyms, salons..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-0 focus-visible:ring-0 text-base h-12"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2 px-2">
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="h-12 px-4 rounded-full border-0 bg-slate-50 text-base focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="city-select"
                >
                  <option value="">All Cities</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                <Button
                  type="submit"
                  className="h-12 px-8 rounded-full bg-primary hover:bg-primary/90 text-white font-medium"
                  data-testid="search-button"
                >
                  Search
                </Button>
              </div>
            </div>
          </motion.form>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="categories-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
              Browse by Category
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Find the perfect business for your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {categories.slice(0, 5).map((category, index) => {
              const isLarge = index === 0;
              const isTall = index === 1;

              return (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`${
                    isLarge
                      ? 'col-span-1 md:col-span-8 row-span-2'
                      : isTall
                      ? 'col-span-1 md:col-span-4 row-span-2'
                      : 'col-span-1 md:col-span-4'
                  }`}
                >
                  <Card
                    onClick={() => handleCategoryClick(category.id)}
                    className="group relative overflow-hidden rounded-2xl cursor-pointer h-full min-h-[200px] border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300"
                    data-testid={`category-card-${category.slug}`}
                  >
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                      style={{
                        backgroundImage: `url(${categoryImages[category.slug] || 'https://images.unsplash.com/photo-1646602201694-43d9acc2b542?w=400'})`,
                      }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-slate-900/20"></div>
                    <div className="relative z-10 p-8 h-full flex flex-col justify-end">
                      <h3 className="text-2xl md:text-3xl font-semibold text-white mb-2">
                        {category.name}
                      </h3>
                      <p className="text-slate-200 text-sm">Explore →</p>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <p className="text-slate-400">
            © 2026 nearconnect. Connecting communities across Northeast India.
          </p>
        </div>
      </footer>

      {showAuth && (
        <AuthModal
          isOpen={!!showAuth}
          onClose={() => setSearchParams({})}
          defaultMode={showAuth}
        />
      )}
    </div>
  );
}
